import { Component, HostListener, OnDestroy, OnInit, TemplateRef, ViewChild } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { ActivatedRoute, Router } from "@angular/router";
import { WindowService } from "@merri/common";
import { Observable, Subject, of } from "rxjs";
import { concatMap, filter, map, take, takeUntil, tap } from "rxjs/operators";
import { LoggingService, SnackBarService } from "src/app/core";
import { CustomerRequestService } from "src/app/features/customer-requests/services";
import { EventContextService, EventUserRoleGroup, EventsService } from "src/app/features/events";
import { EventLayoutTemplatesSelectorService, EventLink } from "src/app/features/events/shared";
import { OnboardingService } from "src/app/features/onboarding";
import { HelperMethods, SidenavService } from "src/app/shared";
import { VisionboardItem, VisionboardMode, VisionboardVersion } from "../../models";
import { VisionboardItemResponse, VisionboardItemResponses } from "../../responses";
import {
  VisionboardAttributesService,
  VisionboardAutoRefreshService,
  VisionboardCategoriesService,
  VisionboardFavoritesDialogService,
  VisionboardItemCompatibilityService,
  VisionboardService,
  VisionboardVersionsService,
  VisionboardWorkspaceItemsStoreService,
  VisionboardWorkspaceTemplateService,
} from "../../services";
import { VisionboardItemAddRemoveClickInput } from "../../shared/models";
import { VisionboardGlobalItemSearchService, VisionboardItemAddRemoveButtonService } from "../../shared/services";
import {
  CompatibleItemSelectionInput,
  VisionboardCompatibleItemSelectionDialogComponent,
} from "../visionboard-compatible-item-selection-dialog/visionboard-compatible-item-selection-dialog.component";

@Component({
    selector: "app-visionboard-main",
    templateUrl: "./visionboard-main.component.html",
    styleUrls: ["./visionboard-main.component.scss"],
    providers: [VisionboardAutoRefreshService, VisionboardGlobalItemSearchService],
    standalone: false
})
export class VisionboardMainComponent implements OnInit, OnDestroy {
  private destroyed$: Subject<void> = new Subject<void>();

  loading: boolean;
  hasVenues: boolean;
  missingEventDataMessage: string;
  smallScreen: boolean;
  templatesMode: boolean;
  freeformMode: boolean;
  vbLoading: boolean;
  isShowroom: boolean;

  @ViewChild("headerRightTemplate", { static: true }) headerRightTemplate: TemplateRef<any>;
  @ViewChild("headerLeftTemplate", { static: true }) headerLeftTemplate: TemplateRef<any>;

  constructor(
    private eventsService: EventsService,
    private visionboardCategoriesService: VisionboardCategoriesService,
    private visionboardAttributesService: VisionboardAttributesService,
    private visionboardWorkspaceItemsStoreService: VisionboardWorkspaceItemsStoreService,
    private visionboardWorkspaceTemplateService: VisionboardWorkspaceTemplateService,
    private visionboardVersionsService: VisionboardVersionsService,
    private visionboardService: VisionboardService,
    private visionboardAutoRefreshService: VisionboardAutoRefreshService,
    private eventLayoutTemplatesSelectorService: EventLayoutTemplatesSelectorService,
    private snackBarService: SnackBarService,
    private route: ActivatedRoute,
    private router: Router,
    private windowService: WindowService,
    private onboardingService: OnboardingService,
    private eventContextService: EventContextService,
    private _sidenavService: SidenavService,
    private customerRequestService: CustomerRequestService,
    private matDialog: MatDialog,
    private _visionboardItemAddRemoveButtonService: VisionboardItemAddRemoveButtonService,
    private _visionboardItemCompatibilityService: VisionboardItemCompatibilityService,
    private _visionboardFavoritesDialogService: VisionboardFavoritesDialogService,
  ) {
    // Empty ctor
  }

  @HostListener("document:click")
  onDocumentClick(): void {
    this.visionboardAutoRefreshService.reset();
  }

  ngOnInit(): void {
    this.eventsService.setEventWindowTitle("Vision Board");
    this.smallScreen = this._isSmallScreen();
    this.loading = true;

    this.eventsService
      .fetchLatest$()
      .pipe(take(1), takeUntil(this.destroyed$))
      .subscribe({
        next: () => {
          if (this.eventContextService.validateUserRoleAndRoute(EventUserRoleGroup.FullAccess, this.route)) {
            // only once we fetched latest event then we should subscribe to version-based events
            this.setupVersionSub();
          }
        },
      });

    this.route.queryParams.pipe(takeUntil(this.destroyed$)).subscribe(i => {
      if (i.m) {
        const mString = i.m as string;
        const mode = VisionboardMode[mString] as VisionboardMode;
        if (VisionboardMode[mode] && this.visionboardService.getCurrentMode() !== mode) {
          this.visionboardService.changeMode(mode);
        }
      } else {
        this.setModeQueryParam();
      }
    });

    this.visionboardService.mode$.pipe(takeUntil(this.destroyed$)).subscribe(mode => {
      this.setMode(mode);

      if (this.route.snapshot.queryParams.m !== VisionboardMode[mode]) {
        this.setModeQueryParam(!this.route.snapshot.queryParams.m);
      }
    });

    this.visionboardService.loading$.pipe(takeUntil(this.destroyed$)).subscribe({
      next: loading => {
        this.vbLoading = loading;
      },
    });

    this.isShowroom = this.eventContextService.isShowroomEvent();

    this._sidenavService.initHeaderContext({
      title: "Vision Board",
      titleLeft: this.eventContextService.getHeaderTitleLeft(EventLink.VisionBoard),
      loading: true,
      rightTemplate: {
        center: {
          template: this.headerRightTemplate,
        },
      },
      leftTemplate: {
        right: {
          template: this.headerLeftTemplate,
        },
      },
    });

    this.customerRequestService.requestPosted$.pipe(takeUntil(this.destroyed$)).subscribe({
      next: posted => {
        const itemResponse = posted.entity as VisionboardItemResponse;

        if (!!itemResponse?.variantSkuId) {
          const item = this.visionboardWorkspaceItemsStoreService.getVisionboardItemFromResponse(itemResponse);

          if (posted.isUpdate) {
            this.visionboardWorkspaceItemsStoreService.updateItem(item);
          } else {
            this.visionboardWorkspaceItemsStoreService.addItem(item);
            this.visionboardWorkspaceTemplateService.addItemsToLane([item]);
          }

          this.visionboardAttributesService.raiseFiltersChanged();
        }
      },
    });

    this.customerRequestService.dialogClosed$.pipe(takeUntil(this.destroyed$)).subscribe({
      next: result => {
        if (result?.submitted && !result.isUpdate) {
          this.visionboardCategoriesService.setSelectedCategory(null);
        }
      },
    });

    this.visionboardVersionsService.selectedVersion$.pipe(takeUntil(this.destroyed$)).subscribe({
      next: (visionboardVersion: VisionboardVersion) => {
        // Whenever the version changes, we should deselect the category to avoid stale item search
        this.visionboardCategoriesService.setSelectedCategory(null);

        if (visionboardVersion?.versionName) {
          this._sidenavService.setTitle(visionboardVersion.versionName);
          this._sidenavService.setHeaderLoading(false);
        } else {
          this._sidenavService.setHeaderLoading(true);
        }
      },
    });

    this.visionboardVersionsService.versionRenamed$.pipe(takeUntil(this.destroyed$)).subscribe({
      next: (visionboardVersion: VisionboardVersion) => {
        this._sidenavService.setTitle(visionboardVersion.versionName);
      },
    });

    this._visionboardItemAddRemoveButtonService.addItemClicked$.pipe(takeUntil(this.destroyed$)).subscribe({
      next: (input: VisionboardItemAddRemoveClickInput) => {
        const item: VisionboardItem = input.item;
        if (this.visionboardWorkspaceItemsStoreService.userHasItem(item.variantSkuId)) {
          this.snackBarService.show({ message: "You've already selected that item", duration: 2000, action: "Hide" });
        } else {
          this.visionboardWorkspaceItemsStoreService.addItem(item);
          this.visionboardWorkspaceTemplateService.addItemsToLane([item]);

          let obs: Observable<boolean>;

          if (
            (this._visionboardFavoritesDialogService.isOpen() || this.templatesMode) &&
            this._visionboardItemCompatibilityService.requiresChildItems(item)
          ) {
            obs = this._showCompatibleItemSelectionDialog(item);
          } else if (item.resizable) {
            obs = this.visionboardService.showResizablePrompt$(this.matDialog);
          } else {
            obs = of(true);
          }

          obs.pipe(take(1)).subscribe();
        }
      },
    });

    this._visionboardItemAddRemoveButtonService.removeItemClicked$
      .pipe(
        concatMap((input: VisionboardItemAddRemoveClickInput) => {
          return this.visionboardService.confirmItemDeleteOrReplace$(this.matDialog).pipe(
            tap(result => {
              if (result.result) {
                this.visionboardWorkspaceItemsStoreService.removeItem(input.item, true);
              }
            }),
          );
        }),
        takeUntil(this.destroyed$),
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();

    this.visionboardVersionsService.clearVersions();
    this.visionboardCategoriesService.clearCategories();

    this.snackBarService.hide();
    this.visionboardService.disableModeChange(false);
  }

  onCategoriesModeClick(): void {
    this.visionboardService.changeMode(VisionboardMode.Category);
  }

  showWorkspace(): boolean {
    return !this.loading && this.hasVenues && (this.showFreeform() || this.showTemplates());
  }

  showFreeform(): boolean {
    return this.freeformMode && !this.smallScreen;
  }

  showTemplates(): boolean {
    return this.templatesMode;
  }

  showNoVenueWarning(): boolean {
    return !this.loading && !this.hasVenues;
  }

  showSmallScreenWarning(): boolean {
    return !this.loading && this.freeformMode && this.smallScreen && this.hasVenues;
  }

  showLoading(): boolean {
    return this.loading;
  }

  private setupVersionSub(): void {
    this.visionboardVersionsService.currentVersions$.pipe(takeUntil(this.destroyed$)).subscribe(versions => {
      const event = this.eventsService.getCurrentEvent();
      this.hasVenues = this.eventsService.validateVenues(event);
      this.missingEventDataMessage = this.eventsService.getMissingEventDataMessage(event);
      this.loading = !versions;

      if (!this.loading && this.hasVenues && !this.visionboardVersionsService.getSelectedVersion()) {
        this.visionboardVersionsService.selectDefaultVersion();
      }

      if (!this.loading) {
        this.onboardingService.ready();
      }
    });

    this.eventLayoutTemplatesSelectorService.workflowComplete$.pipe(takeUntil(this.destroyed$)).subscribe({
      next: () => {
        this.visionboardVersionsService.reloadSelectedVersion();
      },
    });

    this.visionboardWorkspaceItemsStoreService.items$
      .pipe(
        filter(i => !i.loading && !this.eventLayoutTemplatesSelectorService.workflowActive),
        concatMap(() => {
          const items = this.visionboardWorkspaceItemsStoreService.getItems();
          const vbid = this.visionboardVersionsService.getSelectedVersion()?.versionId;

          if (items?.layoutTemplatesRequiredSubCategories?.length && vbid) {
            return this.eventLayoutTemplatesSelectorService.startVisionboardWorkflow$(this.matDialog, "VisionboardMain.items$", vbid);
          }

          return of(true);
        }),
        takeUntil(this.destroyed$),
      )
      .subscribe();

    this.windowService
      .observeMax$(1080)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: () => {
          const smallScreen = this._isSmallScreen();

          if (smallScreen && this.freeformMode) {
            this.matDialog.closeAll();

            this.matDialog.afterAllClosed.pipe(take(1)).subscribe({
              next: () => {
                this.smallScreen = smallScreen;
              },
            });
          } else {
            this.smallScreen = smallScreen;
          }
        },
      });
  }

  private setModeQueryParam(replaceUrl: boolean = true): void {
    HelperMethods.mergeQueryParam(this.router, this.route, this.visionboardService.getQueryParams(), replaceUrl);
  }

  private setMode(mode: VisionboardMode): void {
    this.templatesMode = mode === VisionboardMode.Category;
    this.freeformMode = mode === VisionboardMode.Freeform;
  }

  private _isSmallScreen(): boolean {
    return this.windowService.matchesMax(1080);
  }

  private _showCompatibleItemSelectionDialog(item: VisionboardItem): Observable<boolean> {
    const dialog = this.matDialog.open(VisionboardCompatibleItemSelectionDialogComponent, {
      data: { parentItem: item } as CompatibleItemSelectionInput,
      autoFocus: false,
    });

    LoggingService.log("VisionboardCompatibleItemSelectionDialogComponent - Dialog opened");

    return dialog.afterClosed().pipe(
      map(() => {
        const items = dialog.componentInstance.lane.items;
        this.visionboardWorkspaceTemplateService.addItemsToLane(items);
        return true;
      }),
    );
  }
}
